import { useEffect, useMemo, useState } from "react";
import { Target, AlertTriangle, TrendingUp, TrendingDown, Briefcase, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const ACCENT = "#EC4899";

function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function fmtNum(n, frac = 1) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: frac, maximumFractionDigits: frac });
}
function fmtPct(p) {
  if (p === null || p === undefined || isNaN(p)) return "—";
  const sign = p > 0 ? "+" : "";
  return `${sign}${p.toFixed(1)}%`;
}
function bandFor(pct) {
  if (pct === null || pct === undefined) return "unknown";
  if (pct > 20)  return "critical";   // >20% over budget
  if (pct > 0)   return "over";       // 0-20% over
  if (pct > -10) return "on_track";   // within -10%
  return "under";                     // under-running >10%
}
const BAND_STYLES = {
  critical: { fg: "#F87171", chip: "border-red-500/40 bg-red-950/40 text-red-300",       label: "Critical" },
  over:     { fg: "#FBBF24", chip: "border-amber-500/40 bg-amber-950/40 text-amber-300", label: "Over" },
  on_track: { fg: "#34D399", chip: "border-emerald-500/40 bg-emerald-950/40 text-emerald-300", label: "On Track" },
  under:    { fg: "#60A5FA", chip: "border-sky-500/40 bg-sky-950/40 text-sky-300",        label: "Under" },
  unknown:  { fg: "#94A3B8", chip: "border-border bg-[#0A0B10] text-muted-foreground",   label: "—" },
};

export default function MoldJobBudgetCard({ showMexico = false, dept = 'MOLD' }) {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [bandFilter, setBandFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/departments/${dept}/job-budget-performance`, { params: { show_mexico: showMexico } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [showMexico, dept]);

  const rows = useMemo(() => {
    let r = data.rows || [];
    if (bandFilter !== "all") r = r.filter((x) => bandFor(x.hours_pct) === bandFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => (x.job || "").toLowerCase().includes(q));
    }
    return r;
  }, [data.rows, bandFilter, search]);

  const counts = useMemo(() => {
    const out = { critical: 0, over: 0, on_track: 0, under: 0, unknown: 0 };
    (data.rows || []).forEach((r) => { out[bandFor(r.hours_pct)] += 1; });
    return out;
  }, [data.rows]);

  const t = data.totals || {};
  const totalHrsPct = t.budget_hours ? ((t.actual_hours - t.budget_hours) / t.budget_hours) * 100 : null;
  const totalCostPct = t.budget_cost ? ((t.actual_cost - t.budget_cost) / t.budget_cost) * 100 : null;

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden p-5"
      data-testid="mold-job-budget-card"
    >
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Briefcase size={18} style={{ color: ACCENT }} />
          <h3 className="font-heading font-bold text-xl">Job Performance · Budget vs Actual</h3>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground font-mono">
          Source: Jobs Budget · Phase = {dept === "GFRC" ? "GRGC" : dept === "FRAME" ? "WELD" : dept === "FIBERGLASS" ? "FG" : dept}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        How each {dept === "FRAME" ? "Frame" : dept === "GFRC" ? "GFRC" : dept === "FINISH" ? "Finish" : dept === "FIBERGLASS" ? "Fiberglass" : "Mold"} job is tracking against its budget — hours, material cost, and SF poured
      </p>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-5" data-testid="job-budget-tiles">
        <Tile
          label="Tracked Jobs"  value={(data.rows || []).length} accent={ACCENT}
          icon={<Briefcase size={12} />} testId="tile-jobs"
        />
        <Tile
          label="Hours: A / B" accent={(totalHrsPct ?? 0) > 0 ? "#F87171" : "#34D399"}
          icon={<TrendingUp size={12} />}
          value={`${fmtNum(t.actual_hours)} / ${fmtNum(t.budget_hours)}`}
          sub={fmtPct(totalHrsPct)}
          testId="tile-hours"
        />
        <Tile
          label="Mat$: A / B"  accent={(totalCostPct ?? 0) > 0 ? "#F87171" : "#34D399"}
          icon={<TrendingDown size={12} />}
          value={`${fmtMoney(t.actual_cost)} / ${fmtMoney(t.budget_cost)}`}
          sub={fmtPct(totalCostPct)}
          testId="tile-cost"
        />
        <Tile
          label="Critical / Over" accent="#F87171"
          icon={<AlertTriangle size={12} />}
          value={`${counts.critical} / ${counts.over}`}
          sub="jobs over budget" testId="tile-critical"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="job-budget-filters">
        <div className="inline-flex border border-border">
          {[
            ["all", `All (${(data.rows || []).length})`],
            ["critical", `Critical (${counts.critical})`],
            ["over",     `Over (${counts.over})`],
            ["on_track", `On Track (${counts.on_track})`],
            ["under",    `Under (${counts.under})`],
          ].map(([k, label]) => (
            <button
              key={k} type="button"
              data-testid={`job-budget-band-${k}`}
              onClick={() => setBandFilter(k)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                bandFilter === k ? "bg-[#EC4899] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="job-budget-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job…"
            className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#EC4899]/60"
          />
        </div>
      </div>

      {/* Empty / loading / table */}
      {loading && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground">
          Loading…
        </div>
      )}
      {!loading && (data.rows || []).length === 0 && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground"
             data-testid="job-budget-empty">
          No budget data — upload Jobs Budget.xlsx on the Upload Data page
        </div>
      )}
      {!loading && rows.length === 0 && (data.rows || []).length > 0 && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground">
          No jobs match the filter
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto" data-testid="job-budget-table">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
              <tr>
                <th className="py-2.5 px-3">Job</th>
                <th className="py-2.5 px-3 text-right">Est SF</th>
                <th className="py-2.5 px-3 text-right">Act SF</th>
                <th className="py-2.5 px-3 text-right">Pieces</th>
                <th className="py-2.5 px-3 text-right">Budget Hrs</th>
                <th className="py-2.5 px-3 text-right">Actual Hrs</th>
                <th className="py-2.5 px-3 text-right w-[120px]">Hrs vs Budget</th>
                <th className="py-2.5 px-3 text-right">Budget Mat$</th>
                <th className="py-2.5 px-3 text-right">Actual Mat$</th>
                <th className="py-2.5 px-3 text-right w-[120px]">Mat$ vs Budget</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const hb = BAND_STYLES[bandFor(r.hours_pct)];
                const cb = BAND_STYLES[bandFor(r.cost_pct)];
                const hPct = Math.min(150, Math.abs(r.hours_pct ?? 0));
                const cPct = Math.min(150, Math.abs(r.cost_pct ?? 0));
                return (
                  <tr key={`${r.job}-${i}`}
                      className="border-t border-border/40 hover:bg-[#1A1C28]/40"
                      data-testid={`job-budget-row-${r.job}`}>
                    <td className="py-3 px-3">
                      <div className="font-mono text-white">{r.job}</div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-[180px]"
                           title={(r.categories || []).join(", ")}>
                        {(r.categories || []).join(", ")}
                      </div>
                    </td>
                    <td className="py-3 px-3 font-mono text-right text-muted-foreground">
                      {fmtNum(r.est_sqft, 0)}
                    </td>
                    <td className="py-3 px-3 font-mono text-right">{fmtNum(r.actual_sqft, 0)}</td>
                    <td className="py-3 px-3 font-mono text-right text-muted-foreground">{r.pieces}</td>
                    <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmtNum(r.budget_hours)}</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtNum(r.actual_hours)}</td>
                    <td className="py-3 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[9px] tracking-widest uppercase border font-mono ${hb.chip}`}>
                          {hb.label}
                        </span>
                        <span className="font-mono" style={{ color: hb.fg }}>{fmtPct(r.hours_pct)}</span>
                      </div>
                      <div className="relative h-1 bg-[#1F2230] mt-1 overflow-hidden">
                        <div className="absolute top-0 left-0 h-full transition-all"
                             style={{ width: `${hPct}%`, backgroundColor: hb.fg }} />
                      </div>
                    </td>
                    <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmtMoney(r.budget_cost)}</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtMoney(r.actual_cost)}</td>
                    <td className="py-3 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[9px] tracking-widest uppercase border font-mono ${cb.chip}`}>
                          {cb.label}
                        </span>
                        <span className="font-mono" style={{ color: cb.fg }}>{fmtPct(r.cost_pct)}</span>
                      </div>
                      <div className="relative h-1 bg-[#1F2230] mt-1 overflow-hidden">
                        <div className="absolute top-0 left-0 h-full transition-all"
                             style={{ width: `${cPct}%`, backgroundColor: cb.fg }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, sub, icon, accent, testId }) {
  return (
    <div data-testid={testId} className="bg-[#12141D] p-4 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="font-heading font-bold text-2xl tabular-nums" style={{ color: accent }}>
        {value ?? "—"}
      </div>
      {sub && <div className="text-[11px] font-mono text-muted-foreground">{sub}</div>}
    </div>
  );
}
